import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useCalendarNav } from "./calendar/useCalendarNav";
import { useCalendarEvents } from "./calendar/useCalendarEvents";
import { CalendarSidebar } from "./calendar/CalendarSidebar";
import { CalendarToolbar } from "./calendar/CalendarToolbar";
import { TimedGrid } from "./calendar/TimedGrid";
import { MonthGrid } from "./calendar/MonthGrid";
import { BlockDialog } from "./calendar/BlockDialog";
import { EventDetailDialog } from "./calendar/EventDetailDialog";

export function AvailabilityCalendar({ value = [], onChange, busy = [], blocks = [], bookings = [], onAddBlock, onDeleteBlock }) {
  const { t } = useTranslation();
  const { view, setView, anchor, setAnchor, days, navigate } = useCalendarNav();
  const { events, availDays } = useCalendarEvents({ value, busy, blocks, bookings, t });
  const [detail, setDetail] = useState(null);
  const [blockDraft, setBlockDraft] = useState(null);

  const addSlotAt = (day, hour) => {
    const start = new Date(day); start.setHours(hour, 0, 0, 0);
    if (start.getTime() < Date.now()) return;
    const iso = start.toISOString();
    if ((value || []).some((s) => s.start === iso)) return;
    onChange([...(value || []), { id: `slot_${start.getTime()}`, start: iso, end: new Date(start.getTime() + 3600000).toISOString(), booked: false }]);
  };
  const removeSlot = (id) => onChange((value || []).filter((s) => s.id !== id));
  const repeatSlot = (weeks) => {
    const d = detail?.data;
    if (!d) return;
    const existing = new Set((value || []).map((s) => s.start));
    const s0 = new Date(d.start).getTime();
    const e0 = new Date(d.end).getTime();
    const WEEK = 7 * 24 * 3600 * 1000;
    const added = [];
    for (let i = 1; i <= weeks; i++) {
      const st = new Date(s0 + i * WEEK);
      const iso = st.toISOString();
      if (st.getTime() < Date.now() || existing.has(iso)) continue;
      existing.add(iso);
      added.push({ id: `slot_${st.getTime()}`, start: iso, end: new Date(e0 + i * WEEK).toISOString(), booked: false });
    }
    if (added.length) {
      onChange([...(value || []), ...added]);
      toast.success(t("cal.toastRepeated", { count: added.length }));
    } else {
      toast.info(t("cal.toastRepeatedNone"));
    }
    setDetail(null);
  };
  const pickDay = (day) => { setAnchor(day); setView("day"); };
  const submitBlock = () => { if (blockDraft) { onAddBlock?.(blockDraft); setBlockDraft(null); } };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[264px_1fr] bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
      <CalendarSidebar anchor={anchor} setAnchor={setAnchor} availDays={availDays} />

      <div className="flex flex-col min-w-0">
        <CalendarToolbar view={view} setView={setView} anchor={anchor} setAnchor={setAnchor} navigate={navigate} days={days} />
        {view === "month"
          ? <MonthGrid anchor={anchor} events={events} onPickDay={pickDay} />
          : <TimedGrid days={days} events={events} view={view} onAddSlot={addSlotAt} onOpenBlock={setBlockDraft} onSelectEvent={setDetail} onPickDay={pickDay} />}
      </div>

      <BlockDialog draft={blockDraft} setDraft={setBlockDraft} onSubmit={submitBlock} />
      <EventDetailDialog
        detail={detail}
        onClose={() => setDetail(null)}
        onDeleteBlock={(id) => { onDeleteBlock?.(id); setDetail(null); }}
        onDeleteSlot={(id) => { removeSlot(id); setDetail(null); }}
        onRepeatSlot={repeatSlot}
      />
    </div>
  );
}
